import { BlobDecoration } from "../BlobDecoration";
import { Icon } from "../ui/icons/icon";

/**
 * Apricot accent colors for the compass icon.
 * Warm golden tone that bridges the cool teal blobs with the lime/chartreuse of chart-1.
 */
const ACCENT = {
  bg: "#fcebd8",
  icon: "#c99a6b",
} as const;

/**
 * Not found page for landing pages.
 * Matches the Nature Minimal aesthetic of the landing page design.
 */
export function LandingNotFound() {
  return (
    <div className="nature-minimal min-h-screen bg-background overflow-hidden relative flex flex-col">
      {/* Decorative blobs - hidden on mobile for cleaner look, matching landing page on desktop */}
      <div className="hidden md:block">
        <BlobDecoration
          className="absolute -top-20 right-0 w-80 h-80 opacity-25 translate-x-1/3 text-chart-1"
          variant={1}
        />
        <BlobDecoration className="absolute top-40 right-0 w-48 h-48 opacity-15 text-chart-2" variant={2} />
        <BlobDecoration className="absolute -bottom-32 -left-40 w-80 h-80 opacity-30 text-chart-5" variant={3} />
        <BlobDecoration className="absolute bottom-20 -left-20 w-40 h-40 opacity-20 text-accent" variant={4} />
      </div>
      {/* Scaled blobs for mobile */}
      <div className="md:hidden">
        <BlobDecoration className="absolute -top-10 -right-10 w-48 h-48 opacity-20 text-chart-1" variant={1} />
        <BlobDecoration className="absolute top-1/4 -right-20 w-32 h-32 opacity-15 text-chart-2" variant={2} />
        <BlobDecoration className="absolute bottom-32 -left-16 w-56 h-56 opacity-25 text-chart-5" variant={3} />
        <BlobDecoration className="absolute -bottom-10 right-8 w-40 h-40 opacity-20 text-chart-1" variant={4} />
      </div>

      {/* Header */}
      <header className="relative px-6 md:px-8 py-6 flex justify-between items-center max-w-7xl mx-auto w-full">
        {/* eslint-disable-next-line react/jsx-no-target-blank -- preserve referrer for analytics */}
        <a
          href="https://noma.to"
          target="_blank"
          rel="noopener"
          className="text-sm tracking-wide text-primary opacity-50 hover:opacity-100 transition-opacity"
        >
          noma.to
        </a>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 md:px-8 relative">
        <div className="text-center max-w-md">
          <div
            className="mb-8 inline-flex items-center justify-center w-20 h-20 rounded-full"
            style={{ backgroundColor: ACCENT.bg }}
          >
            <Icon i="compass" className="w-10 h-10" style={{ color: ACCENT.icon }} />
          </div>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-light text-foreground mb-4 tracking-tight">
            Off the map
          </h1>
          <p className="text-secondary text-base md:text-lg mb-10 leading-relaxed">
            This adventure doesn&apos;t exist yet, or the path here has changed.
          </p>

          <a
            href="https://noma.to"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full transition-colors hover:bg-muted border border-input text-primary"
          >
            <span className="text-sm font-medium">Find your way</span>
            <Icon i="external-link" className="w-3.5 h-3.5" />
          </a>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative py-12 md:py-20 px-6 md:px-8">
        {/* Footer blobs - desktop only */}
        <div className="hidden md:block">
          <BlobDecoration className="absolute -bottom-16 -right-16 w-72 h-72 opacity-20 text-chart-1" variant={4} />
          <BlobDecoration className="absolute -bottom-12 -left-20 w-48 h-48 opacity-15 text-chart-5" variant={2} />
        </div>

        <div className="max-w-4xl mx-auto text-center relative">
          <p className="text-sm text-muted-foreground">
            Create your own adventure map at{" "}
            <a href="https://noma.to" className="hover:text-primary transition-colors">
              noma.to
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
